/**
 * REFRESH AUTH — Funzione pura di refresh, condivisa tra Redux thunk e
 * httpClient 401 interceptor.
 *
 * Perche' estratta: sia authSlice.refreshAccessToken (thunk Redux) sia
 * httpClient (interceptor 401) devono poter triggerare un refresh. Se
 * fossero due code path indipendenti potrebbero sparare 2+ refresh in
 * parallelo quando una scadenza coincide con una race di chiamate API.
 * La singleton in-flight promise qui sotto garantisce UN solo POST
 * /user/refresh in volo a prova di race.
 *
 * Nessun import da authSlice: httpClient -> refreshAuth -> tokenManager,
 * senza cicli e senza dipendenze da Redux.
 *
 * I token vivono ESCLUSIVAMENTE in tokenManager (single source of truth).
 * Redux non ha accessToken/refreshToken nel suo state:
 * performRefresh aggiorna tokenManager e basta. Chi vuole orchestrare il
 * refresh dentro il flusso Redux (es. checkAuth) passa per il thunk
 * authSlice.refreshAccessToken, che delega a performRefresh e ritorna il
 * payload al chiamante — senza extraReducer perche' non c'e' niente da
 * aggiornare in Redux.
 *
 * /user/refresh e' un endpoint anonimo (no Authorization header richiesto).
 */

import { CRM_BASE_URL } from '../api/config';
import { getRefreshToken, getWebId, setTokens } from './tokenManager';
import type { LoginResponse } from './types';

let inflight: Promise<LoginResponse> | null = null;

async function doRefresh(): Promise<LoginResponse> {
  const refreshToken = getRefreshToken();
  const webId = getWebId();
  if (!refreshToken || !webId) {
    throw new Error('Nessun refresh token');
  }

  const response = await fetch(`${CRM_BASE_URL}/user/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ webId, refreshToken }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Refresh fallito (${response.status}) ${text}`);
  }
  const loginResponse = (await response.json()) as LoginResponse;
  // apiHost: ri-applicato anche al refresh — se il virtualHost della company
  // cambia (admin reconfig), la SPA si allinea senza richiedere re-login.
  setTokens({
    accessToken: loginResponse.accessToken,
    refreshToken: loginResponse.refreshToken,
    webId,
    apiHost: loginResponse.apiHost,
  });
  return loginResponse;
}

/**
 * Esegue un refresh del token utente. Race-safe: se una chiamata e'
 * gia' in volo, ritorna la stessa Promise invece di triggerarne una
 * nuova. Aggiorna tokenManager (non Redux) — i consumer Redux devono
 * passare per il thunk authSlice.refreshAccessToken se vogliono che
 * lo state Redux sia aggiornato.
 */
export function performRefresh(): Promise<LoginResponse> {
  if (inflight !== null) return inflight;
  inflight = doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}
